import { useEffect, type RefObject } from "react";
import { useMotionValue, type MotionValue } from "framer-motion";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useTranslation } from "react-i18next";

import { useTheme } from "@/lib/theme";

gsap.registerPlugin(ScrollTrigger);

export function useScrollTriggerRefresh() {
  const { i18n } = useTranslation();
  const theme = useTheme();

  useEffect(() => {
    const id = window.setTimeout(() => ScrollTrigger.refresh(), 120);
    return () => window.clearTimeout(id);
  }, [i18n.language, theme]);
}

export function useSectionProgress(
  ref: RefObject<HTMLElement | null>,
): MotionValue<number> {
  const progress = useMotionValue(0);

  useEffect(() => {
    let frame = 0;

    const measure = () => {
      frame = 0;
      const section = ref.current;
      if (!section) return;

      const rect = section.getBoundingClientRect();
      const travel = rect.height - window.innerHeight;
      if (travel <= 0) {
        progress.set(0);
        return;
      }

      progress.set(Math.min(1, Math.max(0, -rect.top / travel)));
    };

    const schedule = () => {
      if (frame) return;
      frame = requestAnimationFrame(measure);
    };

    measure();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
    };
  }, [ref, progress]);

  return progress;
}

export function scrollToSectionIndex(
  section: HTMLElement | null,
  index: number,
  lastIndex: number,
) {
  if (!section || lastIndex <= 0) return;

  const travel = section.offsetHeight - window.innerHeight;
  if (travel <= 0) return;

  window.scrollTo({ top: section.offsetTop + (travel * index) / lastIndex });
}
